/**
 * Size Guide — Dawn v15 component
 * Vanilla JS only, native <dialog>, no dependencies.
 * - Focus trap, ESC close, overlay (backdrop) click close.
 * - Locks background scroll while open.
 * - Respects prefers-reduced-motion.
 * - Optional one-time subtle pulse to draw attention.
 * - Click-to-zoom toggle for the size chart image (desktop);
 *   pinch-zoom is handled natively via touch-action: pinch-zoom on mobile.
 */
(function () {
  'use strict';

  if (customElements.get('auto-size-guide-block')) return;

  var prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  var FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

  function AutoSizeGuideBlock() {
    return Reflect.construct(HTMLElement, [], AutoSizeGuideBlock);
  }

  AutoSizeGuideBlock.prototype = Object.create(HTMLElement.prototype);
  AutoSizeGuideBlock.prototype.constructor = AutoSizeGuideBlock;

  AutoSizeGuideBlock.prototype.connectedCallback = function () {
    this.trigger = this.querySelector('[data-auto-size-guide-open]');
    this.dialog = this.querySelector('.auto-size-guide__dialog');
    this.closeBtn = this.querySelector('[data-auto-size-guide-close]');
    this.zoomWrap = this.querySelector('[data-auto-size-guide-zoom]');
    this.lastFocused = null;

    if (!this.trigger || !this.dialog) return;

    this.trigger.addEventListener('click', this.open.bind(this));
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', this.close.bind(this));
    }

    // Backdrop click closes the dialog (native <dialog> click target is
    // the dialog box itself; a click outside its content rect means the
    // backdrop was clicked).
    this.dialog.addEventListener('click', function (event) {
      var rect = this.dialog.getBoundingClientRect();
      var inside =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!inside) this.close();
    }.bind(this));

    // Native <dialog> fires "cancel" on ESC; also fires "close".
    this.dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      this.close();
    }.bind(this));

    this.dialog.addEventListener('keydown', this.handleKeydown.bind(this));

    if (this.zoomWrap) {
      this.zoomWrap.addEventListener('click', function () {
        // Only toggle click-zoom on pointer-fine (desktop) devices;
        // mobile relies on native pinch-zoom.
        if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
          this.zoomWrap.classList.toggle('is-zoomed');
        }
      }.bind(this));
    }

    this.maybePulse();
  };

  AutoSizeGuideBlock.prototype.maybePulse = function () {
    if (prefersReducedMotion) return;
    var trigger = this.trigger;
    window.setTimeout(function () {
      trigger.classList.add('is-pulsing');
      trigger.addEventListener(
        'animationend',
        function () {
          trigger.classList.remove('is-pulsing');
        },
        { once: true }
      );
    }, 1200);
  };

  AutoSizeGuideBlock.prototype.open = function () {
    this.lastFocused = document.activeElement;
    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
    document.documentElement.classList.add('auto-size-guide-lock-scroll');

    var focusTarget = this.closeBtn || this.dialog.querySelector(FOCUSABLE_SELECTOR);
    if (focusTarget) focusTarget.focus();
  };

  AutoSizeGuideBlock.prototype.close = function () {
    if (typeof this.dialog.close === 'function' && this.dialog.open) {
      this.dialog.close();
    } else {
      this.dialog.removeAttribute('open');
    }
    document.documentElement.classList.remove('auto-size-guide-lock-scroll');

    if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
      this.lastFocused.focus();
    }
  };

  AutoSizeGuideBlock.prototype.handleKeydown = function (event) {
    if (event.key !== 'Tab') return;

    var focusable = Array.prototype.slice.call(
      this.dialog.querySelectorAll(FOCUSABLE_SELECTOR)
    );
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  customElements.define('auto-size-guide-block', AutoSizeGuideBlock);
})();